'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

export type UserRole = 'investment-user' | 'firm-admin' | 'platform-admin';

interface RoleContextValue {
  activeRole: UserRole;
  setActiveRole: (role: UserRole) => void;
}

const STORAGE_KEY = 'nasca_active_role';

const RoleContext = createContext<RoleContextValue>({
  activeRole: 'investment-user',
  setActiveRole: () => {},
});

export const useRole = () => useContext(RoleContext);

export const RoleProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeRole, setActiveRoleState] = useState<UserRole>('investment-user');

  // Hydrate from localStorage after mount (avoids SSR mismatch)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as UserRole | null;
      if (stored && ['investment-user', 'firm-admin', 'platform-admin'].includes(stored)) {
        setActiveRoleState(stored);
      }
    } catch {
      // localStorage unavailable — keep default
    }
  }, []);

  const setActiveRole = (role: UserRole) => {
    setActiveRoleState(role);
    try {
      localStorage.setItem(STORAGE_KEY, role);
    } catch {
      // ignore
    }
  };

  return (
    <RoleContext.Provider value={{ activeRole, setActiveRole }}>
      {children}
    </RoleContext.Provider>
  );
};
